import { FC, useRef, useState } from "react";
import { InstantQuotationVessel } from "../../types/quotations/InstantQuotationVessel";
import { Dialog } from "primereact/dialog";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
    closeSelectVesselModal,
    instantQuotationSelector,
    setSelectedInstantQuotationVessel
} from "../../features/instantQuotationSlice";
import { VesselFinderResponse } from "../../types/VesselFinderResponse";
import { useDebounceCallback } from "usehooks-ts";
import { Trans, useTranslation } from "react-i18next";
import { DataView } from "primereact/dataview";
import "./VesselFinder.css";
import { finderHeader, vesselTemplate } from "./VesselFinderTemplates";
import { useAuthenticator } from "@aws-amplify/ui-react";

export interface VesselFinderDialogMethods {
    showDialog: (show: boolean) => void;
    setVessel: (vessel: InstantQuotationVessel) => void;
}

export const VesselFinder: FC = () => {
    const { t } = useTranslation("locale");
    const [vesselSearch, setVesselSearch] = useState<string>("");
    const [searchResults, setSearchResults] = useState<
        InstantQuotationVessel[] | undefined
    >(undefined);
    const [resultsLoading, setResultsLoading] = useState<boolean>(false);
    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);

    const latestRequestRef = useRef<number>(0); // To keep track of the latest request

    const shipSearchUrl = `${process.env.REACT_APP_IDWAL_API_URL}/ships/search`;

    const dispatch = useAppDispatch();
    const { selectingVessel } = useAppSelector(instantQuotationSelector);

    const toggleModal = () => {
        latestRequestRef.current = 0;
        setResultsLoading(false);
        dispatch(closeSelectVesselModal());
    };

    const fetchVessels = async (searchValue: string, requestId: number) => {
        if (!searchValue) {
            setSearchResults(undefined);
            return;
        }

        setResultsLoading(true);
        const response = await fetch(`${shipSearchUrl}?search=${searchValue}`, {
            headers: {
                Authorization:
                    "Bearer " +
                    user.getSignInUserSession()?.getIdToken().getJwtToken()
            }
        });
        const data = (await response.json()) as VesselFinderResponse;
        const results = data.ships;

        if (requestId === latestRequestRef.current) {
            setSearchResults(results);
            setResultsLoading(false);
        }
    };

    const debounced = useDebounceCallback((value: string) => {
        const currentRequestId = Date.now();
        latestRequestRef.current = currentRequestId;
        setVesselSearch(value);
        fetchVessels(value, currentRequestId);
    }, 500);

    const handleVesselSearchChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (debounced.isPending()) {
            debounced.cancel();
        }

        debounced(event.target.value);
    };

    const handleSelectVessel = (vessel: InstantQuotationVessel) => {
        latestRequestRef.current = 0;
        setResultsLoading(false);
        dispatch(setSelectedInstantQuotationVessel(vessel));
        dispatch(closeSelectVesselModal());
    };

    const resultsCountMessage = () => {
        if (searchResults === undefined || searchResults.length === 0) {
            return <></>;
        }

        return (
            <div>
                <span id="search-results-count">
                    {searchResults.length > 10 ? "10+" : searchResults.length}{" "}
                    {searchResults.length === 1
                        ? t("instantQuotation.vesselSearch.oneResult")
                        : t("instantQuotation.vesselSearch.multipleResults")}
                </span>
            </div>
        );
    };

    const noRecents = () => {
        if (
            vesselSearch &&
            searchResults !== undefined &&
            searchResults.length === 0
        ) {
            return (
                <div className="flex justify-content-center align-items-center">
                    <span className="search-message">
                        <Trans
                            i18nKey={
                                "instantQuotation.vesselSearch.noVesselsFound"
                            }
                            components={[
                                <a
                                    content="Mail link for Idwal Enquiries"
                                    className="summary-link"
                                    key="contact-us"
                                    href="mailto:enquiries@idwalmarine.com"
                                />
                            ]}
                        ></Trans>
                    </span>
                </div>
            );
        }

        if (
            (searchResults !== undefined && searchResults.length > 0) ||
            resultsLoading
        ) {
            return <></>;
        }
    };

    return (
        <Dialog
            draggable={false}
            header={finderHeader(handleVesselSearchChange, vesselSearch)}
            visible={selectingVessel}
            style={{ width: "42.5rem" }}
            onHide={toggleModal}
        >
            {searchResults !== undefined && searchResults.length > 0 && (
                <div>
                    {resultsCountMessage()}
                    <DataView
                        loading={resultsLoading}
                        emptyMessage={
                            t("instantQuotation.vesselSearch.noVesselsFound") +
                            ` "${vesselSearch}"`
                        }
                        value={searchResults}
                        layout="list"
                        itemTemplate={(data: InstantQuotationVessel) =>
                            vesselTemplate(data, handleSelectVessel)
                        }
                    />
                </div>
            )}
            {resultsLoading && (
                <div className="flex justify-content-center align-items-center">
                    <i
                        className="pi pi-spin pi-spinner"
                        style={{ fontSize: "2rem" }}
                    />
                </div>
            )}
            {noRecents()}
        </Dialog>
    );
};
